import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import logger from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Shared axios instance that includes auth credentials via Bearer token
// NOTE: localStorage is used intentionally for cross-tab session persistence
// (e.g., email verification links opening in new tabs). For higher-security
// deployments, consider migrating to httpOnly cookies with a server-side
// refresh mechanism. See: OWASP Token Storage guidelines.
export const authAxios = axios.create();

// Interceptor: attach stored session token as Bearer header
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(() => {
    return localStorage.getItem("session_token") || null;
  });

  const saveToken = useCallback((token) => {
    setSessionToken(token);
    if (token) {
      localStorage.setItem("session_token", token);
    } else {
      localStorage.removeItem("session_token");
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("session_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await authAxios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      logger.error("Auth check failed:", e);
      setUser(null);
      saveToken(null);
    } finally {
      setLoading(false);
    }
  }, [saveToken]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await authAxios.post(`${API}/auth/logout`, {});
    } catch { /* ignore */ }
    setUser(null);
    saveToken(null);
    window.location.href = "/";
  };

  const loginEmail = async (email, password) => {
    const res = await authAxios.post(`${API}/auth/login`, { email, password });
    setUser(res.data);
    if (res.data.session_token) saveToken(res.data.session_token);
    return res.data;
  };

  const registerEmail = async (name, email, password) => {
    const res = await authAxios.post(`${API}/auth/register`, { name, email, password });
    setUser(res.data);
    if (res.data.session_token) saveToken(res.data.session_token);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginEmail, registerEmail, logout, setUser, checkAuth, sessionToken, setSessionToken: saveToken, API }}>
      {children}
    </AuthContext.Provider>
  );
};
